/* LT-28 — Master Library stale-pricing banner.
 *
 * Follows the Pro Sums migration banner visual pattern (amber-300 border on
 * amber-50 bg with an AlertCircle icon). Deliberately NOT extracted from
 * `TenderProvsums.jsx` because the two banners live on different pages and
 * expose different actions — keeping them as sibling patterns rather than
 * one shared component avoids over-abstraction.
 *
 * Dismissal:
 *   Session-only. Handled by the parent via a boolean state. Dismissal never
 *   clears the underlying pricing_stale flags — the STALE chips + Stale
 *   filter remain visible even after the banner is dismissed.
 */
import React from "react";
import { AlertCircle, X, ClipboardList } from "lucide-react";

export function MasterLibraryStaleBanner({ count, onReview, onDismiss }) {
  if (!count || count <= 0) return null;
  return (
    <div
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
      data-testid="master-library-stale-banner"
      role="status"
      aria-live="polite"
    >
      <AlertCircle size={16} className="text-amber-700 mt-0.5 shrink-0" />
      <div className="flex-1">
        <div
          className="text-amber-900"
          style={{ fontSize: 12.5, fontWeight: 600 }}
        >
          {count === 1
            ? "1 item is affected by recent cost changes."
            : `${count} items are affected by recent cost changes.`}
        </div>
        <p className="text-amber-800 mt-0.5" style={{ fontSize: 11.5 }}>
          Underlying material or labour costs have changed since these library
          items were last priced. Review the recalculations before accepting.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={onReview}
          data-testid="master-library-stale-review-btn"
          className="inline-flex items-center gap-1 h-7 px-3 rounded-md border border-amber-300 bg-amber-700 text-white hover:bg-amber-800 transition-colors"
          style={{ fontSize: 11.5, fontWeight: 500 }}
        >
          <ClipboardList size={11} /> Review
        </button>
        <button
          onClick={onDismiss}
          data-testid="master-library-stale-dismiss-btn"
          className="text-amber-700 hover:text-amber-900 p-1 rounded"
          title="Dismiss banner for this session (STALE chips will remain)"
          aria-label="Dismiss banner"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
